import { ProgressBar } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { IconButton } from "@src/appV2/redesign/components/IconButton";

import { type PostShiftReviewStep, type StepAction } from "../types";

export interface PostShiftReviewHeaderProps {
  currentStep: PostShiftReviewStep;
  computedSteps: PostShiftReviewStep[];
  onBackOrClose: (action: StepAction) => void;
}

export function PostShiftReviewHeader({
  currentStep,
  computedSteps,
  onBackOrClose,
}: PostShiftReviewHeaderProps) {
  const currentIndex = computedSteps.indexOf(currentStep);

  function getHeaderConfig() {
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (currentStep) {
      case "pick-rating": {
        return null; // No back button for rating step (first step)
      }

      case "write-review": {
        return {
          iconType: "close",
          action: { type: "CLOSE_FLOW" },
        } as const;
      }

      case "end": {
        return null; // No back button for completion step
      }

      default: {
        return {
          iconType: "chevron-left",
          action: { type: "BACK_STEP" },
        } as const;
      }
    }
  }

  const config = getHeaderConfig();

  const progressTotal = computedSteps.length - 1; // Exclude "end" step from progress calculation
  const shouldShowProgressBar = currentStep !== "end" && progressTotal > 1;
  const progressCompleted = Math.min(currentIndex + 1, progressTotal);

  return (
    <Stack direction="row" alignItems="center" sx={{ px: 4, py: 2 }}>
      {/* Always reserve space for the button to prevent height jump */}
      <Box sx={{ height: 40, display: "flex", alignItems: "center" }}>
        {config && (
          <IconButton
            variant="outlined"
            size="small"
            iconType={config.iconType}
            sx={{ mr: 4 }}
            onClick={() => {
              onBackOrClose(config.action);
            }}
          />
        )}
      </Box>

      {shouldShowProgressBar && (
        <Box sx={{ flex: 1, mx: 3 }}>
          <ProgressBar completed={progressCompleted} total={progressTotal} />
        </Box>
      )}
    </Stack>
  );
}
